<template>
  <v-dialog
    width="550"
    v-model="opened"
    persistent
    @keydown.esc="$emit('update:opened', false)"
  >
    <input
      type="file"
      accept=".pdf"
      style="display: none"
      multiple
      ref="selectedDocuments"
      @change="onDocumentsPicked"
    />

    <v-card flat @drop.prevent="addFile" @dragover.prevent>
      <v-card-title class="py-4 title text-xs-left">
        <h4>Import Tax Return from Tax Software</h4>
      </v-card-title>

      <v-card-text class="pt-0">
        <v-alert
          :value="true"
          outline
          block
          :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
          color="primary"
          class="text-xs-left mb-2"
        >
          The import process will take about 5 minutes. You will receive a
          notification once completed.
        </v-alert>

        <v-layout wrap class="text-xs-left" v-if="!hideEntitySelect">
          <v-flex class="mb-4">
            <div class="font-weight-bold mb-2">Send to:</div>

            <v-autocomplete
              class="default-input body-1"
              hide-details
              :loading="loadingCompanies"
              :disabled="loadingCompanies"
              solo
              :items="entitiesWithClient"
              v-model="selectedEntityId"
              placeholder="Select entity"
              item-text="name"
              item-value="id"
            >
            </v-autocomplete>
          </v-flex>
        </v-layout>

        <v-layout wrap class="text-xs-left">
          <v-flex class="mb-4">
            <div class="font-weight-bold mb-2">
              Tax year:<span class="error--text ml-1">*</span
              ><v-tooltip top
                ><v-icon slot="activator" size="15" class="ml-1">info</v-icon
                ><span
                  >Specify which folder this document should be organized
                  into</span
                ></v-tooltip
              >
            </div>

            <v-autocomplete
              class="default-input body-1"
              solo
              :rules="rules.presence"
              :items="yearOptions"
              v-model="taxYear"
              placeholder="Select year"
            />
          </v-flex>
        </v-layout>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          class="text-capitalize primary"
          @click="upload"
          :loading="documentsImporting"
          :disabled="!canUpload"
        >
          Begin Import
        </v-btn>
        <v-btn
          class="text-capitalize primary--text"
          outline
          @click="
            $emit('update:opened', false);
            documentsToImport = [];
          "
        >
          {{ $t('cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import get from 'lodash/get';
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import { rules, yearOptions } from '@/constants/variables';
import UtilsMixins from '@/mixins/UtilsMixins';

export default {
  mixins: [UtilsMixins],
  props: {
    opened: {
      type: Boolean,
    },

    currentCompany: {
      type: Object,
    },

    hideEntitySelect: {
      type: Boolean,
    },
  },

  data() {
    return {
      rules,
      yearOptions,

      taxYear: null,

      documentData: '',
      documentsToImport: [],
      documentsImporting: false,

      selectedEntityId: this.currentCompany ? this.currentCompany.id : null,
    };
  },

  methods: {
    isEmpty,

    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },

    async onDocumentsPicked(e) {
      this.documentsImporting = true;
      const documents = e.target.files;

      if (documents) {
        this.documentsToImport.push(...documents);
        this.documentsImporting = false;
      }
    },

    async addFile(e) {
      this.documentImporting = true;
      let droppedFiles = e.dataTransfer.files;
      if (!droppedFiles) return;

      this.documentsToImport.push(...droppedFiles);
      this.documentImporting = false;
    },

    async upload() {
      this.documentsImporting = true;
      try {
        await API.integration.processDocument({
          id: '123',
          documentName: 'blank',
          companyId: this.selectedEntityId,
          path: `/_soraban/Tax Return Delivery/${this.taxYear}`,
          documentType: 'tax_return_delivery_import',
          taxYear: this.taxYear,
        });

        this.$store.dispatch('application/setSuccessNotification', {
          message:
            'Successfully started import. Please wait a few minutes for the document to be processed.',
        });
        this.$emit('refresh-table');
        this.$emit('update:opened', false);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: has(e, 'message')
            ? get(e, 'message')
            : `There was a problem uploading the document${
                this.documentsToImport.length > 1 ? 's' : ''
              }.`,
          errorToSend: e,
        });
      } finally {
        this.userImportDialogOpened = false;
        this.documentsImporting = false;
        this.documentsToImport = [];
      }
    },

    getDisplayName(entity) {
      if (this.isIndividual(entity)) {
        return entity.tax_return_name;
      } else {
        return `${entity.name} (${entity.user.full_name})`;
      }
    },

    isIndividual(entity) {
      return entity.entity_type === 'Individual';
    },
  },

  computed: {
    ...mapState('companies', ['companies', 'loadingCompanies']),

    ...mapGetters({
      currentUser: 'user',
    }),

    entitiesWithClient() {
      return this.companies
        .filter(
          c =>
            c.user &&
            (!this.currentUser.overview_setting.show_assigned_clients ||
              c.user.assigned_advisor_id === this.currentUser.id),
        )
        .map(entity => ({
          id: entity.id,
          name: `${this.getDisplayName(entity)}${
            entity.user && entity.user.for_testing ? ' (Test)' : ''
          }`,
          entity_type: entity.entity_type,
          for_testing: entity.user ? entity.user.for_testing : false,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    canUpload() {
      return this.selectedEntityId && this.taxYear;
    },
  },
};
</script>
